'use client';

import { PropertyCard } from '@/components/properties/PropertyCard';
import { SearchFilters } from '@/components/properties/SearchFilters';
import { Button } from '@/components/ui/button';
import type { IProperty } from '@/lib/types';
// NOTE: We now proxy property requests through our own API to avoid
// client-side Sanity CORS/security issues. Use `/api/properties`.
import { ArrowLeft, Building } from 'lucide-react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { EmptyState } from '@/components/ui/empty-state';

import { Suspense } from 'react';
import { ScrollReveal } from '@/components/animations/ScrollReveal';

function PropertiesPageContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [properties, setProperties] = useState<IProperty[]>([]);
  const [displayCount, setDisplayCount] = useState(9);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAndFilterProperties = async () => {
      setLoading(true);
      const qs = searchParams.toString();
      const res = await fetch(`/api/properties${qs ? `?${qs}` : ''}`);
      if (!res.ok) {
        console.error('Failed to fetch properties from API', await res.text());
        setProperties([]);
        setLoading(false);
        return;
      }
      const json = await res.json();
      const allProperties: IProperty[] = json.data?.properties ?? [];
      const reversedProperties = [...allProperties].reverse(); // Show newest first
      setProperties(reversedProperties);
      setLoading(false);
      setDisplayCount(9); // Reset count on new search
    };

    fetchAndFilterProperties();
  }, [searchParams]);

  const loadMore = () => {
    setDisplayCount(prev => prev + 9);
  };

  const displayedProperties = properties.slice(0, displayCount);
  const hasMore = displayCount < properties.length;

  return (
    <div className="container mx-auto px-4 py-8">
      <ScrollReveal direction="none" distance={0}>
        <div className="flex items-center gap-4 mb-8">
          <Button variant="outline" size="icon" onClick={() => router.back()}>
            <ArrowLeft className="h-4 w-4" />
            <span className="sr-only">Return to previous page</span>
          </Button>
        </div>
        <div className="bg-card text-foreground p-8 rounded-lg mb-8 shadow-lg">
          <h1 className="text-4xl font-bold mb-4">Browse Properties</h1>
          <p className="text-lg text-muted-foreground mb-6">
            Find the perfect property that fits your needs.
          </p>
          <SearchFilters />
        </div>
      </ScrollReveal>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {Array.from({ length: 6 }).map((_, index) => (
            <ScrollReveal key={index} delay={index * 0.1}>
              <PropertyCard property={null} />
            </ScrollReveal>
          ))}
        </div>
      ) : displayedProperties.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {displayedProperties.map((property, idx) => (
              <ScrollReveal key={property.id} delay={(idx % 3) * 0.1}>
                <PropertyCard property={property} />
              </ScrollReveal>
            ))}
          </div>

          {hasMore && (
            <ScrollReveal>
              <div className="flex justify-center mt-8 mb-12 w-full">
                <Button
                  size="lg"
                  onClick={loadMore}
                  className="bg-accent hover:bg-accent/90 text-accent-foreground min-w-[200px]"
                >
                  Load More Properties
                </Button>
              </div>
            </ScrollReveal>
          )}
        </>
      ) : (
        <div className="col-span-full py-8">
          <ScrollReveal>
            <EmptyState
              icon={Building}
              title="No Properties Found"
              description="We couldn't find any properties matching your search. Try adjusting your filters."
              actionLabel="Reset Filters"
              actionLink="/properties"
            />
          </ScrollReveal>
        </div>
      )}
    </div>
  );
}

export default function PropertiesPage() {
  return (
    <Suspense fallback={<div>Loading properties...</div>}>
      <PropertiesPageContent />
    </Suspense>
  );
}
